import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, AircallAccountStand } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import "./KundeAircallAccount.css";

/**
 * Der Abschnitt „Aircall-Account“ in der Kundenakte (Spec „Abrechnungsquellen“,
 * 29.09.2026) — neben „Portal-Mandanten“ (Muster KundePortalMandanten.tsx).
 *
 * Der Account ist die Abrechnungseinheit bei Aircall: genau EINER je Kunde, und aus ihm
 * werden ALLE Gesprächszeiten abgerechnet, unabhängig von Quelle und Ziel des Gesprächs
 * (Dirk 29.09.2026). Ein Auftrag mit Quelle AIRCALL verknüpft sich mit ihm.
 *
 * Anlegen und Ändern im STRIKTEN Modal; die Knöpfe sieht nur ADMIN (R7) — der Server prüft
 * dasselbe. Ein Löschen bietet die Oberfläche NICHT an (Spec: „Ändern statt Löschen“).
 *
 * KEIN ZUSTAND ÜBERLEBT DEN KONTEXTWECHSEL (Hausregel): Wechselt der Kunde, fallen Stand,
 * offenes Modal, Meldungen und Feldfehler weg; späte Antworten verwirft der Lauf-Riegel.
 *
 * Eigener CSS-Block `kaa` — mit keiner Ansicht geteilt.
 */

export type AircallAccountClient = Pick<ApiClient, "getAircallAccount" | "setzeAircallAccount">;

export type KundeAircallAccountProps = {
  client: AircallAccountClient;
  customerId: string;
  /** Nur ADMIN pflegt den Account (R7). */
  istAdmin: boolean;
  /** Über dem Abschnitt steht ein fremdes Panel (Stammdaten-Formular) — Knöpfe sperren. */
  verdeckt?: boolean | undefined;
};

type Entwurf = { accountKey: string; label: string; note: string };
type FeldName = "accountKey" | "label" | "note";
const FELDNAMEN: readonly FeldName[] = ["accountKey", "label", "note"];

function entwurfAus(stand: AircallAccountStand): Entwurf {
  const a = stand.account;
  return a === null
    ? { accountKey: "", label: "", note: "" }
    : { accountKey: a.accountKey, label: a.label ?? "", note: a.note ?? "" };
}

function gleich(a: Entwurf, b: Entwurf): boolean {
  return a.accountKey === b.accountKey && a.label === b.label && a.note === b.note;
}

export function KundeAircallAccount(p: KundeAircallAccountProps): ReactElement {
  const { client, customerId } = p;
  const [stand, setStand] = useState<AircallAccountStand | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Entwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [feldFehler, setFeldFehler] = useState<Partial<Record<FeldName, string>>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const lauf = useRef(0);
  const getAircallAccount = client.getAircallAccount;

  useEffect(() => {
    const meiner = ++lauf.current;
    setStand(null);
    setLadeFehler(null);
    setDialog(null);
    setMeldung(null);
    setFeldFehler({});
    getAircallAccount(customerId)
      .then((s) => { if (meiner === lauf.current) setStand(s); })
      .catch((e: unknown) => {
        if (meiner !== lauf.current) return;
        setStand({ account: null });
        setLadeFehler(e instanceof ApiError ? e.message
          : "Der Aircall-Account konnte nicht geladen werden.");
      });
    return () => { lauf.current += 1; };
  }, [getAircallAccount, customerId]);

  const geaendert = dialog !== null && stand !== null && !gleich(dialog, entwurfAus(stand));
  useBeforeUnload(geaendert);

  function oeffnen(): void {
    if (stand === null) return;
    setDialog(entwurfAus(stand));
    setFeldFehler({});
    setMeldung(null);
  }

  /** Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel“). */
  function abbrechen(): void {
    if (speichert) return;
    if (!darfAbbrechen(geaendert)) return;
    setDialog(null);
    setFeldFehler({});
  }

  function setze(feld: FeldName, wert: string): void {
    setDialog((d) => (d === null ? d : { ...d, [feld]: wert }));
  }

  async function speichern(): Promise<void> {
    if (dialog === null || stand === null) return;
    // Schnelle Rückmeldung, nicht die Entscheidung — die trifft der Server.
    if (dialog.accountKey.trim() === "") {
      setFeldFehler({ accountKey: "Die Kennung bei Aircall ist erforderlich." });
      return;
    }
    const meiner = lauf.current;
    setSpeichert(true);
    setFeldFehler({});
    setMeldung(null);
    try {
      const label = dialog.label.trim();
      const note = dialog.note.trim();
      const neu = await client.setzeAircallAccount(customerId, {
        version: stand.account?.version ?? null,
        accountKey: dialog.accountKey.trim(),
        label: label === "" ? null : label,
        note: note === "" ? null : note,
      });
      if (meiner !== lauf.current) return;
      setStand(neu);
      setDialog(null);
    } catch (e) {
      if (meiner !== lauf.current) return;
      if (e instanceof ApiError && e.status === 409) {
        const frisch = e.conflict?.current as AircallAccountStand | undefined;
        if (frisch !== undefined) setStand(frisch);
        setMeldung("Der Aircall-Account wurde inzwischen geändert — der aktuelle Stand ist "
          + "geladen. Bitte die Eingaben prüfen und erneut speichern.");
      } else if (e instanceof ApiError && (e.fields ?? []).length > 0) {
        const neu: Partial<Record<FeldName, string>> = {};
        let unzuordenbar = false;
        for (const f of e.fields ?? []) {
          const treffer = FELDNAMEN.find((n) => n === f.field);
          if (treffer === undefined) { unzuordenbar = true; continue; }
          if (neu[treffer] === undefined) neu[treffer] = f.message;
        }
        setFeldFehler(neu);
        if (unzuordenbar || Object.keys(neu).length === 0) setMeldung(e.message);
      } else {
        setMeldung(e instanceof ApiError ? e.message : "Unerwarteter Fehler beim Speichern.");
      }
    } finally {
      setSpeichert(false);
    }
  }

  const account = stand?.account ?? null;

  return (
    <section className="kaa" aria-label="Aircall-Account">
      <h3 className="kaa__titel">Aircall-Account</h3>
      <p className="kaa__hinweis">
        Die Abrechnungseinheit bei Aircall: Aus dem Account werden alle Gesprächszeiten
        abgerechnet, unabhängig von Quelle und Ziel des Gesprächs.
      </p>

      {meldung !== null && dialog === null && (
        <div className="kaa__meldung" role="alert">{meldung}</div>
      )}
      {ladeFehler !== null && <div className="kaa__meldung" role="alert">{ladeFehler}</div>}
      {stand === null && <p className="kaa__hinweis" role="status">Lädt …</p>}

      {stand !== null && account === null && ladeFehler === null && (
        <p className="kaa__hinweis">Für diesen Kunden ist kein Aircall-Account hinterlegt.</p>
      )}
      {account !== null && (
        <dl className="kaa__daten">
          <dt>Kennung</dt><dd className="kaa__kennung">{account.accountKey}</dd>
          <dt>Anzeigename</dt><dd>{account.label ?? "—"}</dd>
          <dt>Notiz</dt><dd>{account.note ?? "—"}</dd>
        </dl>
      )}

      {p.istAdmin && stand !== null && (
        <div className="kaa__aktionen">
          <button type="button" className="kaa__knopf"
            disabled={dialog !== null || p.verdeckt === true} onClick={oeffnen}>
            {account === null ? "Aircall-Account anlegen" : "Aircall-Account ändern"}
          </button>
        </div>
      )}

      {dialog !== null && (
        <Modal titel={account === null ? "Aircall-Account anlegen" : "Aircall-Account ändern"}
          breite="standard" onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          <div className="kaa__dialog">
            {meldung !== null && <div className="kaa__meldung" role="alert">{meldung}</div>}
            <Feld id="kaa-kennung" label="Kennung bei Aircall" bearbeitbar
              wert={dialog.accountKey} onChange={(w) => setze("accountKey", w)}
              {...(feldFehler.accountKey !== undefined ? { fehler: feldFehler.accountKey } : {})} />
            <Feld id="kaa-anzeigename" label="Anzeigename (optional)" bearbeitbar
              wert={dialog.label} onChange={(w) => setze("label", w)}
              {...(feldFehler.label !== undefined ? { fehler: feldFehler.label } : {})} />
            <Feld id="kaa-notiz" label="Notiz (optional)" bearbeitbar
              wert={dialog.note} onChange={(w) => setze("note", w)}
              {...(feldFehler.note !== undefined ? { fehler: feldFehler.note } : {})} />
          </div>
          <ModalFuss speichernText="Speichern" speichernGesperrt={speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}
